/**
 * BasicNet GTM analytics bridge — the single home for the theme-side GTM
 * tracking code. Standalone subset of the full bnt integration for themes
 * that don't carry it yet; four concerns, all read by the GTM custom web
 * pixel or by GTM itself:
 *
 * 1. Page context (`bnt:page_context`): precise page type, market country,
 *    locale language and currency — data the sandboxed pixel cannot derive
 *    reliably from URLs. The values come from Liquid via the
 *    `[data-bnt-config]` JSON stamped by snippets/bnt-analytics.liquid.
 *    Published INLINE by snippets/bnt-page-context-publish.liquid at the top
 *    of <body> on every page but search results (see section 1 below).
 *    NOTE: this publish also tells the pixel "the theme covers product
 *    lists" (it disables the pixel's own collection/search list fallbacks),
 *    so it must ship together with the impressions publisher below.
 *
 * 2. Cart mutation UI context (`bnt:cart_context`): which UI performed a
 *    cart mutation. Publishes ONLY the context; the product data keeps
 *    flowing through Shopify's native pixel events (product_added_to_cart /
 *    product_removed_from_cart), so nothing else is needed and nothing can
 *    double-fire. Horizon dispatches `shopify:cart:lines-update` from the
 *    component that performed the mutation; the payload fields live on the
 *    EVENT itself (`event.context`, `event.promise` — the constructor
 *    Object.assigns them; `event.detail` is unrelated custom data). The
 *    event's target tells sibling UIs apart: quick-add dialog/component →
 *    `quick_add`; context `product` → `pdp`; context `cart` → `sidebar`
 *    when the target sits in the cart drawer (drawer and cart page share
 *    the same cart-items component and both use context `cart`), else
 *    `cart_page`. The publish waits for the mutation promise so it
 *    lands right next to the pixel's native cart event, inside its
 *    freshness window. The pixel waits briefly for this publish and uses
 *    its `method`.
 *
 * 3. List impressions (`bnt:list_viewed`): every product card renders a
 *    bnt-item-hook JSON payload, and hooks are grouped by the listing
 *    container Liquid stamped around them — the SAME `data-item-list-*`
 *    stamps the click decoration below reads, so an impression and a click
 *    on the same card can never report different lists. A card outside a
 *    stamped container publishes nothing: adding the hook to a shared card
 *    snippet is therefore safe, and a surface starts reporting only once
 *    someone declares it a list. One impression per container is published
 *    the first time any of its CARDS reaches the viewport (watching the
 *    container instead would count a load-more page appended below the fold
 *    as seen, and would count the closed mega menu on every pageview) —
 *    except on the pages the config marks `listsOnLoad`, the homepage and
 *    the category pre-listing pages, where every module reports as soon as
 *    it renders. Morphed or late-rendered content (Section Rendering,
 *    load-more, facet refresh) is picked up by a MutationObserver and
 *    re-published as `refresh`.
 *
 * 4. Click-time listing attribution: appends `item_list_name` /
 *    `item_list_id` to product links AT CLICK TIME so the PDP lands with
 *    the listing context in its URL, where GTM reads it. Liquid stamps the
 *    context on a listing container (`data-item-list-name`, optionally
 *    `data-item-list-id` — see snippets/item-list-attributes.liquid); the
 *    hrefs themselves are rewritten constantly (plp-color-swatch hover,
 *    product-card variant selection, algolia-liquid-card hit links,
 *    filter/sort section morphs), so the params can only be attached at the
 *    moment of navigation. Document-level delegation for the same reason:
 *    cards that arrive later (load more, morphs, Algolia hydration) need no
 *    re-binding.
 *
 * 5. Algolia search results (`bnt:search`): on the markets where Algolia owns
 *    the results page, the real query, result count and hit order — Shopify's
 *    native `search_submitted` describes its own, different result set. Items
 *    carry ids only; the pixel resolves the rest from the variant id through
 *    the Storefront API, because the metafield the item payload is built from
 *    is not in the Algolia index. Read from InstantSearch's results rather
 *    than from the rendered cards, so it does not depend on card hydration.
 *
 * Loaded as a deferred module: every concern reacts to user activity or
 * viewport visibility, and the publish queue absorbs the wait for
 * Shopify.analytics, so nothing here needs to run during parse.
 */

/* --- publish queue -------------------------------------------------------
 * Shopify.analytics may not exist yet when something wants to publish;
 * queue until it does, give up after ~10s.
 *
 * The queue is also what makes this file safe to prerender
 * (snippets/speculation-rules.liquid). Everything here either runs at module
 * evaluation (page context) or as soon as the page renders (list impressions
 * on the pages config marks `listsOnLoad`), and a prerendered page reaches
 * both while it is hidden — before the shopper has decided to go there, and
 * often for a page they never open. Draining only after activation leaves
 * every event describing a real visit; the payloads are captured at their
 * original moment, so an impression still reports the list as it was when it
 * rendered. Nothing has to be undone when a prerender is discarded, because
 * the whole document goes with it.
 *
 * `document.prerendering` is absent outside Chromium, where this is an
 * ordinary page load and the gate is a no-op. */

const pendingPublishes = [];
let publisherReady = false;
let activated = !document.prerendering;

/**
 * Hands one event to the pixel, and echoes it on `document` as `bnt:published`
 * for in-page consumers that need the same moments — assets/clarity.js marks
 * list impressions and colour swaps from it. Fired at the actual publish, so a
 * prerendered page echoes nothing until it is shown either.
 *
 * @param {string} name
 * @param {object} payload
 */
function send(name, payload) {
  window.Shopify.analytics.publish(name, payload);
  document.dispatchEvent(new CustomEvent('bnt:published', { detail: { name, payload } }));
}

/**
 * @param {string} name
 * @param {object} payload
 */
function publish(name, payload) {
  if (publisherReady && activated) {
    send(name, payload);
  } else {
    pendingPublishes.push([name, payload]);
  }
}

function flushPublishes() {
  if (!publisherReady || !activated) return;
  while (pendingPublishes.length) {
    const [name, payload] = pendingPublishes.shift();
    send(name, payload);
  }
}

if (!activated) {
  document.addEventListener(
    'prerenderingchange',
    () => {
      activated = true;
      flushPublishes();
    },
    { once: true }
  );
}

(function waitForPublisher(attempts) {
  if (typeof window.Shopify?.analytics?.publish === 'function') {
    publisherReady = true;
    flushPublishes();
    return;
  }
  if (attempts >= 100) return;
  setTimeout(() => waitForPublisher(attempts + 1), 100);
})(0);

/* --- Liquid-derived page config ------------------------------------------ */

const config = JSON.parse(document.querySelector('script[data-bnt-config]')?.textContent || '{}');

/* --- shared: item_list_id from the live URL -------------------------------
 * For collection grids the id reflects the CURRENT sort/filter state
 * (WebRanking spec): an explicit sort beats applied filters beats plain
 * navigation — flagged to WebRanking, whose spec doesn't rank the two. */

const SORT_TO_LIST_ID = {
  'price-ascending': 'sorting_increasing_price',
  'price-descending': 'sorting_decreasing_price',
  'best-selling': 'most_popular',
};

/** @returns {string} */
function listIdFromListingState() {
  const params = new URLSearchParams(window.location.search);

  const bySort = SORT_TO_LIST_ID[params.get('sort_by')];
  if (bySort) return bySort;

  for (const key of params.keys()) {
    /* filter.v.availability is the drawer's IMPLICIT in-stock toggle
     * (PR #144): invisible to the shopper, excluded from the filter badge
     * and remove-pills — and from filtered_applied here for the same
     * reason. Mirrored in listIdFromUrl in the pixel repo. */
    if (key === 'filter.v.availability') continue;
    if (key.startsWith('filter.')) return 'filtered_applied';
  }

  return 'navigation';
}

/* --- shared: search origin -------------------------------------------------
 * How the shopper reached THIS results page: pressing Enter / the submit
 * button is a `direct` search, clicking "View all" in the overlay is a
 * `suggested` one, and both carry the position of the UI they came from
 * (agency QA 2026-09-17). The search UI lives on the PREVIOUS page, so the
 * origin travels in sessionStorage and is consumed here - it decorates the
 * search event the pixel pushes for this page rather than adding one of its
 * own, which would double-count every search. */

const SEARCH_ORIGIN_KEY = 'bnt:search-origin';

/** @returns {object|null} */
function takeSearchOrigin() {
  let raw = null;
  try {
    raw = sessionStorage.getItem(SEARCH_ORIGIN_KEY);
    /* Always cleared, used or not: an origin left behind by a search the
     * shopper abandoned would mis-attribute the next one. */
    sessionStorage.removeItem(SEARCH_ORIGIN_KEY);
  } catch {
    return null; // storage unavailable (private mode, blocked cookies)
  }
  if (!raw || config.pageType !== 'search_results') return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const searchOrigin = takeSearchOrigin();

/* --- 1. page context ------------------------------------------------------ */

/* The container Liquid marks when a third party owns the result set for this
 * page — today only the Algolia results grid. Server-rendered, so it is in the
 * DOM by the time this deferred module runs. */
const externalListContainer = document.querySelector('[data-item-list-source]');

/* Normally already published inline by snippets/bnt-page-context-publish,
 * before the document finishes parsing: this module runs too late for the
 * pixel's 300 ms window on heavy pages. Published here on search results
 * (searchProvider needs the parsed DOM) and whenever the inline script found
 * no publisher — late beats never, and this queue waits for one. */
if (!window.bntPageContextPublished) publish('bnt:page_context', {
  pageType: config.pageType,
  country: config.country,
  language: config.language,
  currency: config.currency,
  /* Tells the pixel to drop Shopify's native `search_submitted` on this page:
   * it reports Shopify's own result set, which is not what the shopper sees.
   * Published here rather than with the results so the pixel can suppress from
   * the first event, including when Algolia returns nothing or fails. */
  searchProvider: externalListContainer?.dataset.itemListSource || 'shopify',
  /* Decorates this page's search event: {position, method, type, label}.
   * Absent when the shopper landed here any other way (a shared URL, the
   * back button), which is a `direct` search with no position. */
  searchOrigin,
});

/* --- 2. cart mutation UI context ------------------------------------------ */

document.addEventListener('shopify:cart:lines-update', (event) => {
  const context = event.context || '';
  const target = event.target instanceof Element ? event.target : null;

  let method;
  if (context === 'dialog' || target?.closest('quick-add-dialog, quick-add-component')) {
    /* The event bubbles from the component that performed the add: a
     * product form inside the quick-add UI keeps context `product`, so the
     * ancestry check is what identifies quick-add. */
    method = 'quick_add';
  } else if (context === 'product') {
    method = 'pdp';
  } else if (context === 'cart') {
    /* Both selectors matter: mutations routed through Shopify.actions are
     * retargeted to `theme-drawer#cart-drawer` (standard-actions-override.js),
     * which is the ANCESTOR of cart-drawer-component, so testing only the
     * inner element reports drawer edits as cart_page. */
    method = target?.closest('theme-drawer#cart-drawer, cart-drawer-component') ? 'sidebar' : 'cart_page';
  } else {
    /* Anything else (the documented `standard-action` context, an app, a
     * direct SFAPI call) has no shopper-facing UI we can name. Guessing
     * would attribute it to the drawer; publish nothing and let the pixel
     * fall back. */
    return;
  }

  const publishContext = () => publish('bnt:cart_context', { method });

  if (typeof event.promise?.then === 'function') {
    // Publish after the mutation actually succeeded.
    event.promise.then(publishContext, () => {
      /* failed mutation */
    });
  } else {
    publishContext();
  }
});

/* --- 3. product view after an in-place colour swap ------------------------
 * Switching colour on a PDP is a sibling *product*, and the theme now swaps the
 * product section in place rather than navigating (assets/product-sibling-swap.js).
 * No navigation means Shopify emits no `product_viewed`, so every colour a shopper
 * looks at after the first would go unreported — to GA4 and to every other
 * destination the pixel feeds.
 *
 * The swap dispatches `bnt:product-swapped` on the section once the new colour is in
 * the DOM; the transport ids come from the section's own payload, which arrived with
 * it. Same slim shape as every other item (snippets/bnt-item.liquid): the pixel
 * enriches from the Storefront API, so nothing derived lives here.
 *
 * The pixel must map `bnt:product_viewed` the way it maps Shopify's `product_viewed`.
 * Until it does, this publishes into the void — which is the current behaviour anyway,
 * so the two sides can ship independently. */

document.addEventListener('bnt:product-swapped', (event) => {
  const section = event.target instanceof Element ? event.target : null;
  const hook = section?.querySelector('script[data-bnt-pdp-item]');
  if (!hook) return;

  let item;
  try {
    item = JSON.parse(hook.textContent || '');
  } catch {
    /* A payload that won't parse is a Liquid bug, not a shopper problem. */
    return;
  }

  if (!item?.productId) return;
  publish('bnt:product_viewed', item);
});

/* --- 4. list impressions -------------------------------------------------- */

/* Both maps below need one key, and it has to satisfy two requirements that
 * neither the identity nor the element meets on its own.
 *
 * It cannot be `name|id`: the identity is not unique. item-list-attributes
 * .liquid never receives a name and defaults the id, so a page routinely
 * carries several lists under one `standard|suggested` — /pages/shoe-care has
 * two, and 8 of the 14 page templates with product sections are in the same
 * position. Sharing a key there made the two containers overwrite each other:
 * the fingerprint guard stopped protecting either of them, and a second
 * container with the same item set was dropped for good (the `return` below
 * comes after the observer is disconnected and the hooks are claimed, so it
 * never retries).
 *
 * It cannot be the element either: Section Rendering replaces the container
 * node, which is exactly when the guard has to hold.
 *
 * The Shopify section wrapper satisfies both — its id survives a morph of its
 * own contents and differs per section — with the container's position among
 * the stamped ones inside it telling sibling lists apart. Containers rendered
 * outside any section (the cart drawer) fall back to the identity alone;
 * there is only ever one of each.
 *
 * @param {Element} container @param {{name: string, id: string}} list
 */
function listKeyFor(container, list) {
  const section = container.closest('[id^="shopify-section-"]');
  if (!section) return `page|${list.name}|${list.id}`;
  const stamped = section.querySelectorAll('[data-item-list-name]');
  return `${section.id}#${Array.prototype.indexOf.call(stamped, container)}|${list.name}|${list.id}`;
}

/* How many times each list has already published, so a morph or a load-more
 * reports `refresh`. Keyed as above, so the count survives the morph that
 * replaced the node — keying it on the element reported every morph as a
 * fresh `load`. */
const publishCounts = new Map();

/* Last published item set per list: a re-render that changes nothing the
 * shopper sees must not re-publish. The known offender is the filters drawer
 * injecting filter.v.availability and morphing an identical grid (PR #144) —
 * agency QA 2026-09 flagged the double view_item_list. */
const lastListFingerprints = new Map();

/** @param {Element} container @returns {{name: string, id: string}} */
function listFor(container) {
  return {
    name: container.dataset.itemListName,
    /* No id stamped means the PLP grid, whose id is the live sort/filter
     * state — the same derivation the click path uses. */
    id: container.dataset.itemListId || listIdFromListingState(),
  };
}

/**
 * Publishes one group's impression, unless it would repeat the last one.
 *
 * The guard is the reason both entry points below go through here: the
 * fingerprint drops a re-render that changes nothing the shopper sees, and
 * publishCounts is what turns the second publish of a container into a
 * `refresh`.
 *
 * @param {Element} container
 * @param {object[]} items
 */
function publishList(container, items) {
  const list = listFor(container);
  const key = listKeyFor(container, list);
  const fingerprint = items
    .map((item) => String(item.variantId || item.item_variant_id || item.item_id || item.item_name || ''))
    .join(',');
  if (lastListFingerprints.get(key) === fingerprint) return;
  lastListFingerprints.set(key, fingerprint);
  const seen = publishCounts.get(key) || 0;
  publishCounts.set(key, seen + 1);
  publish('bnt:list_viewed', {
    listName: list.name,
    listId: list.id,
    method: seen ? 'refresh' : 'load',
    currency: config.currency,
    items,
  });
}

/**
 * Publishes the group once any of its cards reaches the viewport.
 *
 * The cards are what's observed, never the container: on load-more the
 * container is a full-height grid that is already intersecting, so watching
 * it would report 24 cards appended 3000px below the fold as seen. Cards in
 * a closed dialog or mega menu have no box at all, so they simply never
 * intersect until it opens.
 *
 * @param {Element} container
 * @param {Element[]} cards
 * @param {object[]} items
 */
function publishListWhenVisible(container, cards, items) {
  const pending = { observer: null, cards };
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      pendingGroups.delete(pending);
      publishList(container, items);
    },
    { threshold: 0 }
  );
  pending.observer = observer;
  for (const card of cards) observer.observe(card);
  pendingGroups.add(pending);
}

/* A group whose cards are dropped before ever being seen (a facet morph
 * while scrolled past them) would otherwise keep its observer — and through
 * it the detached nodes and their items — alive for the session. */
const pendingGroups = new Set();
function dropDetachedGroups() {
  for (const pending of pendingGroups) {
    if (pending.cards.some((card) => card.isConnected)) continue;
    pending.observer.disconnect();
    pendingGroups.delete(pending);
  }
}

function scanForItemHooks() {
  dropDetachedGroups();

  const hooks = document.querySelectorAll('script[data-bnt-item]:not([data-bnt-claimed])');
  if (!hooks.length) return;

  const groups = new Map(); // stamped container → {cards, items, seen}
  for (const hook of hooks) {
    hook.setAttribute('data-bnt-claimed', '');

    /* A list is a container Liquid explicitly stamped. Cards outside one
     * (a decorative mini-card in an editorial block, a surface nobody
     * stamped yet) publish nothing: the click path bails on exactly the
     * same test, and an impression the click can't match would attribute
     * the PDP view to a list GA4 never saw the shopper leave. */
    const container = hook.closest('[data-item-list-name]');
    if (!(container instanceof HTMLElement)) continue;

    /* A surface that publishes its own impressions from a better source than
     * the DOM — the Algolia results page, which reports the hits Algolia
     * actually returned rather than the cards that happen to have rendered.
     * The stamp still drives click attribution; only the impression moves. */
    if (container.dataset.itemListSource) continue;

    /* A surface that is attributed on click but must NOT report
     * impressions: the search overlay, whose suggestions the spec has no
     * view_item_list for (agency QA 2026-09-17 — products previewed while
     * typing are not a listing the shopper browsed). Separate from
     * data-item-list-source, which doubles as the "another engine owns
     * this page's results" flag that suppresses Shopify's native search
     * event, and would do so on every page from a stamp in the header. */
    if (container.dataset.itemListImpressions === 'off') continue;

    let item;
    try {
      item = JSON.parse(hook.textContent);
    } catch {
      continue; // malformed hook — skip
    }

    if (!groups.has(container)) groups.set(container, { cards: [], items: [], seen: new Set() });
    const group = groups.get(container);

    /* Sections often render every card twice — a desktop grid and a
     * CSS-hidden mobile carousel (e.g. look-and-shop) — so the same product
     * would count double. One entry per product per list. */
    const dedupeKey =
      String(item.variantId || item.item_variant_id || item.item_id || item.item_name || '') ||
      JSON.stringify(item);
    if (group.seen.has(dedupeKey)) continue;
    group.seen.add(dedupeKey);
    group.items.push(item);
    group.cards.push(hook.parentElement || hook);
  }

  groups.forEach((group, container) => {
    /* Homepage and the category pre-listing pages report every module as
     * soon as it renders: their lists are the page's content, not something
     * the shopper scrolls down to discover, and the agency wants them
     * counted on arrival. Everywhere else the viewport still decides. */
    if (config.listsOnLoad) publishList(container, group.items);
    else publishListWhenVisible(container, group.cards, group.items);
  });
}

let scanTimer = null;
/** @param {MutationRecord[]} records */
function scheduleScan(records) {
  /* Lazy images, carousel clones and third-party widgets mutate the document
   * constantly; only added subtrees that actually carry a hook are worth a
   * full-document rescan. */
  const addedAHook = records.some((record) =>
    Array.prototype.some.call(
      record.addedNodes,
      (node) =>
        node instanceof Element &&
        (node.matches?.('script[data-bnt-item]') || node.querySelector?.('script[data-bnt-item]'))
    )
  );
  if (!addedAHook || scanTimer) return;
  scanTimer = setTimeout(() => {
    scanTimer = null;
    scanForItemHooks();
  }, 300);
}

scanForItemHooks();

// Late-rendered lists: Section Rendering morphs, load-more, drawers.
new MutationObserver(scheduleScan).observe(document.documentElement, { childList: true, subtree: true });

/* --- 5. click-time listing attribution ------------------------------------
 *
 * A container with NO `data-item-list-id` derives it from the page URL at
 * click time (the PLP case), so it's always current after filter/sort
 * morphs.
 *
 * `data-item-list-forward` marks the other mode: sibling colour swatches
 * are links to *different products*, so a colour switch on the PDP is a
 * full navigation that would shed the params the shopper arrived with.
 * Forward containers copy both params from the current URL onto the target
 * — a colour hop keeps the original listing attribution, and a PDP reached
 * without params forwards nothing.
 *
 * Capture phase on document: runs before product-card.js's element-level
 * `navigateToProduct` (bubble) and before the browser follows the anchor,
 * so both the native and the programmatic navigation read the decorated
 * href. `auxclick` covers middle-click; `contextmenu` decorates before the
 * shopper can pick "Open in new tab" or "Copy link" from the menu. A tab
 * opened any other way (e.g. dragging the link to the tab bar) is accepted
 * data loss. */

const CONTAINER_SELECTOR = '[data-item-list-name], [data-item-list-forward]';

/* Quick-add CTAs carry crawlable PDP hrefs but their JS intercepts the click
 * (add to cart / choose-options layer) — never a navigation. */
const EXCLUDED_LINKS = '.quick-add a, a.add-to-cart-button';

/**
 * @param {HTMLAnchorElement} anchor
 * @param {HTMLElement} container
 */
function decorate(anchor, container) {
  if (!anchor.getAttribute('href')) return;

  let url;
  try {
    url = new URL(anchor.href);
  } catch {
    return;
  }

  /* Product links only — never collection "view all" links, wishlist icons,
   * or editorial banners that share a stamped container. The pathname check
   * matches locale-prefixed (`/it/products/…`) and collection-scoped
   * (`/collections/x/products/y`) URLs alike. */
  if (url.origin !== window.location.origin) return;
  if (!url.pathname.includes('/products/')) return;

  let name = container.dataset.itemListName;
  let id = container.dataset.itemListId;

  if (name == null) {
    // Forward container: carry the attribution the shopper arrived with, if any.
    const current = new URLSearchParams(window.location.search);
    name = current.get('item_list_name');
    id = current.get('item_list_id');
    if (!name || !id) return;
  }

  /* `set` overwrites, so repeated intents (contextmenu then click, or a second
   * click) stay idempotent, and existing params (?variant, Algolia attribution)
   * are preserved. */
  url.searchParams.set('item_list_name', name);
  url.searchParams.set('item_list_id', id || listIdFromListingState());

  anchor.setAttribute('href', url.toString());
}

/** @param {Event} event */
function onNavigationIntent(event) {
  const target = event.target instanceof Element ? event.target : null;
  const container = target?.closest(CONTAINER_SELECTOR);
  if (!(container instanceof HTMLElement)) return;

  let anchor = target.closest('a[href]');
  if (anchor?.matches(EXCLUDED_LINKS)) return;

  if (!anchor) {
    /* Click on a non-anchor card area: product-card.js#navigateToProduct
     * navigates to refs.productCardLink.href, which it reads AFTER this
     * capture-phase handler has run. */
    anchor = target.closest('product-card')?.querySelector('a[ref="productCardLink"]') ?? null;
  }

  if (anchor instanceof HTMLAnchorElement) decorate(anchor, container);
}

document.addEventListener('click', onNavigationIntent, true);
document.addEventListener('auxclick', onNavigationIntent, true);
document.addEventListener('contextmenu', onNavigationIntent, true);

/* Hover and pointer-down too — not for the data, which the click already
 * captures, but so the speculation rules see the FINAL url.
 *
 * Shopify prefetches product urls platform-wide at `moderate` eagerness,
 * which fires after ~200ms of hover, and the theme's prerender rules
 * (snippets/speculation-rules.liquid) do the same. Both key their cache on
 * the exact url. Decorating only at click time meant the browser speculated
 * `/products/x` and the click then navigated to
 * `/products/x?item_list_name=…`: a different url, so every speculation
 * started from a product card was thrown away and the shopper waited for the
 * full navigation anyway. `pointerover` puts the params on the href before
 * the hover timer elapses; `pointerdown` covers touch, where there is no
 * hover to wait through.
 *
 * `pointerover` rather than `pointerenter` because this is delegated on the
 * document and `pointerenter` does not bubble. Repeats are free — `decorate`
 * `set`s the params — and the click-time pass stays as the safety net for the
 * hrefs plp-color-swatch and the variant pickers rewrite while the pointer is
 * still on the card. */
document.addEventListener('pointerover', onNavigationIntent, true);
document.addEventListener('pointerdown', onNavigationIntent, true);

/* --- 6. Algolia search results --------------------------------------------
 *
 * On the markets where Algolia owns the results page, the shopper is looking
 * at Algolia's hits while Shopify's native `search_submitted` describes its
 * own, different result set — a different query count and different products
 * (69 vs 259 for "dan" on sebago-it). So the theme publishes the real one.
 *
 * The items carry IDS ONLY — the same contract the card hooks now use
 * (snippets/bnt-item.liquid): everything else the spec wants (title, price,
 * the item_category* fields) is derived from the `basic_integration.gtm_info`
 * metafield and the Storefront API, which the pixel resolves from the variant
 * id. This path was ids-only first, because that metafield is not in the
 * Algolia index; the hooks followed once the duplicated Liquid derivations
 * started drifting from the pixel's.
 *
 * This is deliberately independent of the Liquid cards: the hits are read from
 * InstantSearch's own results, so the event is complete and correctly ordered
 * whether or not a card has finished rendering.
 */

if (externalListContainer?.dataset.itemListSource === 'algolia') {
  const publishedSearches = new Set();

  /** @param {object} results InstantSearch `helper.lastResults` */
  function publishSearchResults(results) {
    if (!results || !Array.isArray(results.hits)) return;

    /* One publish per query+page. InstantSearch re-renders on facet paints and
     * widget updates, not just on new results, so without this a single search
     * reports several times. */
    const key = `${results.query}|${results.page}|${results.queryID || ''}`;
    if (publishedSearches.has(key)) return;
    publishedSearches.add(key);

    /* Rank across the whole result set, not within the page, so page 2 starts
     * at 32 rather than back at 0. `hits` is already in Algolia's ranked order;
     * the hit objects carry no rank of their own (`position` is the variant's
     * position inside its product, and the app only adds `productPosition`
     * later, on its way into the template). */
    const firstRank = (results.page ?? 0) * (results.hitsPerPage ?? results.hits.length);

    const seen = new Set();
    const items = [];
    results.hits.forEach((hit, rankOnPage) => {
      const variantId = String(hit.objectID ?? '');
      if (!variantId || seen.has(variantId)) return;
      seen.add(variantId);
      items.push({
        productId: String(hit.id ?? ''),
        variantId,
        // 0-based per the spec ("position ... starting from 0") — the
        // ranks used to start at 1 (agency QA 2026-09-17).
        index: firstRank + rankOnPage,
      });
    });

    publish('bnt:search', {
      query: results.query || '',
      resultCount: results.nbHits ?? items.length,
      page: results.page ?? 0,
      listName: externalListContainer.dataset.itemListName,
      listId: externalListContainer.dataset.itemListId,
      method: publishedSearches.size > 1 ? 'refresh' : 'load',
      currency: config.currency,
      provider: 'algolia',
      /* Algolia's own request id, so the agency can line a GA4 search up with
       * the Algolia analytics record for the same query. */
      queryId: results.queryID || '',
      items,
    });
  }

  /* AlgoliaHelpers is a classic deferred script further down the document, so
   * it does not exist yet when this module runs. Same shape as the publisher
   * wait above: poll briefly, then give up. */
  (function waitForAlgolia(attempts) {
    const search = window.algoliaShopify?.instantsearch?.search;

    if (window.AlgoliaHelpers && search) {
      window.AlgoliaHelpers.onInstantSearch('render', () => {
        publishSearchResults(search.helper?.lastResults);
      });
      /* The listener can only attach once InstantSearch exists, which is at
       * best the same tick as its first render — so read what is already there
       * rather than waiting for a render the page has had. */
      publishSearchResults(search.helper?.lastResults);
      return;
    }

    if (attempts >= 100) return;
    setTimeout(() => waitForAlgolia(attempts + 1), 100);
  })(0);
}

/* --- 7. search interactions ------------------------------------------------
 *
 * The spec's `search` event carries how the shopper searched, not just what
 * they typed (agency QA 2026-09-17). Three shapes, all originating in the
 * overlay:
 *
 *   Enter / submit button  -> the results page reports `direct` + position
 *   "View all"             -> the results page reports `selected`/`suggested`,
 *                             label `view_all`
 *   a suggested product    -> reported HERE, at click time: it navigates to a
 *                             PDP, so no results page follows. The pixel fills
 *                             `label` (product name) and `product` (SKU) from
 *                             the ids, the same way it builds list items.
 *
 * The first two only stash an origin (see SEARCH_ORIGIN_KEY): the search event
 * for that page is the pixel's to push, and a second one here would double it.
 *
 * Listeners are document-level and capture-phase for the same reason the click
 * attribution above is: the overlay's cards, the Algolia panel and the native
 * panel are all replaced as the shopper types, so nothing can be bound to them
 * directly.
 */

/** @param {EventTarget|null} target @returns {string} */
function searchPositionFor(target) {
  const element = target instanceof Element ? target : null;
  if (!element) return '';
  if (element.closest('#search-modal')) return 'search_overlay';
  if (element.closest('search-page-input-component')) return 'header_search';
  return '';
}

/** @returns {string} the query as currently typed in the overlay */
function currentSearchTerm() {
  /* Two panels, two inputs: the native predictive search renders
   * `name="q"`, while on Algolia markets the app replaces the panel with its
   * own `.aa-Input` — the selector assets/algolia-autocomplete.js already
   * uses to find the same field. Matching only the native one reported every
   * Algolia search with an empty term. */
  const input = document.querySelector(
    '#search-modal input[name="q"], #search-modal .aa-Input, #search-modal input[type="search"]',
  );
  return input instanceof HTMLInputElement ? input.value.trim() : '';
}

/** @param {object} origin */
function rememberSearchOrigin(origin) {
  try {
    sessionStorage.setItem(SEARCH_ORIGIN_KEY, JSON.stringify(origin));
  } catch {
    /* storage unavailable — the search still reports, just without origin */
  }
}

/* The tracking hook of the card a click landed in. The hook is a sibling of
 * the card's link, not an ancestor of it, so `closest` can't find it: walk up
 * and look for a hook among each ancestor's own children. */
function hookForTarget(target) {
  let node = target instanceof Element ? target : null;
  while (node && node !== document.body) {
    const hook = node.querySelector(':scope > script[data-bnt-item]');
    if (hook) return hook;
    node = node.parentElement;
  }
  return null;
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('#search-modal')) return;

    const position = searchPositionFor(target);
    const searchTerm = currentSearchTerm();

    /* "View all": the native panel's footer button, or - on Algolia markets -
     * the link the app draws into the products header, which is the only
     * anchor in the overlay pointing at the results page. */
    const viewAll =
      target.closest('.predictive-search__search-button') ||
      target.closest(`a[href*="${window.Shopify?.routes?.root ?? '/'}search"]`);
    if (viewAll) {
      rememberSearchOrigin({ position, method: 'selected', type: 'suggested', label: 'view_all' });
      return;
    }

    /* Algolia's submit button (the native panel has no submit control - only
     * Enter, handled below). */
    if (target.closest('.aa-SubmitButton')) {
      rememberSearchOrigin({ position, method: 'load', type: 'direct' });
      return;
    }

    /* A suggested product: reported now, since the shopper leaves for a PDP. */
    const hook = hookForTarget(target);
    if (!hook || !target.closest('a[href], button')) return;
    let item;
    try {
      item = JSON.parse(hook.textContent ?? '');
    } catch {
      return; // malformed hook
    }
    publish('bnt:search_interaction', {
      searchTerm,
      method: 'selected',
      type: 'suggested',
      position,
      productId: item.productId,
      variantId: item.variantId,
    });
  },
  true
);

/* Enter in the overlay's input. Not a form submit listener: the component
 * navigates programmatically (assets/predictive-search.js), so the form never
 * submits. A highlighted suggestion turns Enter into a click on that card,
 * which the listener above reports instead - the origin stashed here is then
 * dropped unused on the PDP. */
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Enter') return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.matches('input[name="q"]')) return;
    const position = searchPositionFor(target);
    if (!position) return;
    rememberSearchOrigin({ position, method: 'load', type: 'direct' });
  },
  true
);
